import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AuthGuard } from "@/router/AuthGuard";
import { FullScreenLoader } from "@/components/ui/FullScreenLoader";
import { AppShell } from "@/components/layout/AppShell";

// Split BLOK-2 — marketing app router. Faqat marketing + chatbot route'lar.
// Auth = verify-only: token yo'q → core login (auth.prismos.*) ga redirect
// (AuthGuard ichida hozircha /login; core bilan ulanish BLOK-3 da CORE_AUTH_URL).

const LoginPage = lazy(() => import("@/modules/auth/pages/LoginPage"));

// Public legal — Meta App Review (Faza 7 Sprint 4)
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/legal/TermsOfServicePage"));
const DataDeletionRequestPage = lazy(
  () => import("@/pages/legal/DataDeletionRequestPage"),
);

// Marketing (Faza 6 — Sprint 1)
const MarketingDashboardPage = lazy(
  () => import("@/modules/marketing/pages/MarketingDashboardPage"),
);
const AdAccountsPage = lazy(
  () => import("@/modules/marketing/pages/AdAccountsPage"),
);
const CampaignsListPage = lazy(
  () => import("@/modules/marketing/pages/CampaignsListPage"),
);
const CampaignDetailPage = lazy(
  () => import("@/modules/marketing/pages/CampaignDetailPage"),
);
const CreativesPage = lazy(
  () => import("@/modules/marketing/pages/CreativesPage"),
);
const CreativeDetailPage = lazy(
  () => import("@/modules/marketing/pages/CreativeDetailPage"),
);
const PlanPage = lazy(
  () => import("@/modules/marketing/plan/PlanPage"),
);
const ScenariosPage = lazy(
  () => import("@/modules/marketing/pages/ScenariosPage"),
);

// Raqobatchilar — deep Instagram raqobat tahlili (ADR-0060, 3 daraja)
const CompetitorsOverviewPage = lazy(
  () => import("@/pages/competitors/CompetitorsOverviewPage"),
);
const CompetitorDetailPage = lazy(
  () => import("@/pages/competitors/CompetitorDetailPage"),
);
const CompetitorVideoPage = lazy(
  () => import("@/pages/competitors/CompetitorVideoPage"),
);

// Istoriya arxivi — IG istoriyalarini doimiy saqlash + sana filtri
const StoriesArchivePage = lazy(
  () => import("@/pages/stories/StoriesArchivePage"),
);

// Sales-data — sotuv mijozlar + xulosalar (marketing app ichida)
const SalesDataClientsPage = lazy(
  () => import("@/pages/sales-data/ClientsPage"),
);
const SalesDataClientDetailPage = lazy(
  () => import("@/pages/sales-data/ClientDetailPage"),
);
const SalesDataSummariesPage = lazy(
  () => import("@/pages/sales-data/SummariesPage"),
);

// Chatbot — vaqtinchalik yashirilgan (user qarori). Lazy import'lar bundle'ni
// shishirmaslik uchun olib tashlandi; modul tiklansa qaytariladi.

const lazyEl = (node: React.ReactNode) => (
  <Suspense fallback={<FullScreenLoader />}>{node}</Suspense>
);

export const router = createBrowserRouter([
  // Public
  {
    path: "/login",
    element: lazyEl(<LoginPage />),
  },

  // Public legal — Meta App Review (Faza 7 Sprint 4)
  { path: "/privacy", element: lazyEl(<PrivacyPolicyPage />) },
  { path: "/terms", element: lazyEl(<TermsOfServicePage />) },
  { path: "/data-deletion-request", element: lazyEl(<DataDeletionRequestPage />) },

  // Auth — App shell ostida (verify-only)
  {
    path: "/",
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    children: [
      // Marketing dashboard = bosh sahifa
      { index: true, element: <Navigate to="/marketing" replace /> },

      // Marketing — Faza 6 Sprint 1
      { path: "marketing", element: lazyEl(<MarketingDashboardPage />) },
      { path: "marketing/accounts", element: lazyEl(<AdAccountsPage />) },
      { path: "marketing/campaigns", element: lazyEl(<CampaignsListPage />) },
      { path: "marketing/campaigns/:id", element: lazyEl(<CampaignDetailPage />) },
      { path: "marketing/creatives", element: lazyEl(<CreativesPage />) },
      { path: "marketing/creatives/:id", element: lazyEl(<CreativeDetailPage />) },
      { path: "marketing/scenarios", element: lazyEl(<ScenariosPage />) },
      // Raqobatchilar — deep tahlil (overview → akkaunt → video)
      { path: "marketing/stories", element: lazyEl(<StoriesArchivePage />) },
      { path: "marketing/competitors", element: lazyEl(<CompetitorsOverviewPage />) },
      { path: "marketing/competitors/:id", element: lazyEl(<CompetitorDetailPage />) },
      {
        path: "marketing/competitors/:id/posts/:postId",
        element: lazyEl(<CompetitorVideoPage />),
      },
      // Reja / Fakt — bajaruvchi oylik reja vs fakt (ADR marketing-reja-fakt)
      { path: "marketing/plan", element: lazyEl(<PlanPage />) },
      // Eski "Zadachalar" manzili — Reja/Fakt'ga redirect (backward compat)
      { path: "marketing/tasks", element: <Navigate to="/marketing/plan" replace /> },

      // Sales-data — mijozlar va xulosalar
      { path: "sales-data/clients", element: lazyEl(<SalesDataClientsPage />) },
      { path: "sales-data/clients/:id", element: lazyEl(<SalesDataClientDetailPage />) },
      { path: "sales-data/summaries", element: lazyEl(<SalesDataSummariesPage />) },

      // Chatbot — vaqtinchalik YASHIRILGAN (user qarori). Barcha /chatbot/*
      // va settings/chatbot/* manzillar marketing dashboard'ga redirect qilinadi.
      // Backend /api/chatbot/* endpoint'lar saqlangan; UI tiklash uchun mavjud
      // route ta'riflari sharhlangan (lazy import keyinroq qayta yoqiladi).
      { path: "chatbot", element: <Navigate to="/marketing" replace /> },
      { path: "chatbot/*", element: <Navigate to="/marketing" replace /> },
      { path: "settings/chatbot/channels", element: <Navigate to="/marketing" replace /> },
    ],
  },

  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);
